/**
 * Zengen — Geschenk-Logik für den Warenkorb-Fortschrittsbalken.
 *
 * - Klick auf eine Geschenk-Option fügt die 0,00 €-Variante hinzu (oder tauscht
 *   gegen eine bereits gewählte) bzw. entfernt sie bei erneutem Klick.
 * - Nur EIN Geschenk gleichzeitig, immer Menge 1.
 * - Fällt der Warenkorbwert unter die Schwelle, wird das Geschenk automatisch
 *   wieder entfernt.
 *
 * WICHTIG: Keine statischen Imports — der Klick-Listener wird synchron beim
 * Laden registriert und kann nicht durch ein fehlschlagendes Modul sterben.
 * Das Theme-Re-Rendering läuft über einen dynamischen Import von
 * @shopify/events (mit Reload-Fallback).
 */
(() => {
  const routes = (window.Theme && window.Theme.routes) || {};
  let busy = false;

  // --- Ein-/Ausklappen des Fortschritts-Fensters --------------------------
  const COLLAPSE_KEY = 'zg-cart-progress-collapsed';

  function isCollapsed() {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  /** Klappzustand auf alle gerenderten Balken anwenden (übersteht Re-Renders). */
  function applyCollapseState() {
    const collapsed = isCollapsed();
    document.querySelectorAll('.cart-progress').forEach((el) => {
      el.classList.toggle('is-collapsed', collapsed);
      const header = el.querySelector('.cart-progress__header');
      if (header) header.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  function toggleCollapse() {
    const next = !isCollapsed();
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch (_) {
      /* Storage evtl. blockiert – Zustand gilt dann nur bis zum Re-Render */
    }
    applyCollapseState();
  }

  /** Schwelle + erlaubte Geschenk-Varianten aus dem gerenderten Balken lesen. */
  function getConfig() {
    const el = document.querySelector('.cart-progress[data-gift-variants]');
    if (!el) return null;
    const threshold = parseInt(el.dataset.giftThreshold || '0', 10);
    const variants = (el.dataset.giftVariants || '')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter(Boolean);
    if (!threshold || !variants.length) return null;
    return { threshold, variants };
  }

  async function getCart() {
    const res = await fetch(`${routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  /** Alle Cart-Sektions-IDs (Drawer + Cart-Seite), damit alle neu rendern. */
  function sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) ids.add(c.dataset.sectionId);
    });
    return [...ids];
  }

  /** Cart neu rendern: bevorzugt über das Theme-Event, sonst Reload. */
  async function refresh(data) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
      );
      let cartObj = null;
      try {
        cartObj = CartLinesUpdateEvent.createCartFromAjaxResponse(data);
      } catch (_) {
        /* optionales Feld */
      }
      deferred.resolve({
        cart: cartObj,
        detail: { sections: data.sections, items: data.items, source: 'gift-progress' },
      });
    } catch (e) {
      console.warn('[gift-progress] Re-Render via Event fehlgeschlagen, lade Seite neu.', e);
      window.location.reload();
    }
  }

  /** Cart-Mutation ausführen + Re-Render anstoßen. */
  async function mutate(url, payload) {
    const body = JSON.stringify({
      ...payload,
      sections: sectionIds().join(','),
      sections_url: location.pathname,
    });
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
      credentials: 'same-origin',
    });
    const data = await res.json();
    await refresh(data);
    return data;
  }

  // WICHTIG: Zeilen werden über ihren line-item KEY angesprochen, nicht über die
  // Varianten-ID. Die Geschenk-Varianten sind echte, regulär verkaufte Produkte –
  // eine Änderung per Varianten-ID würde auch eine normal gekaufte Zeile derselben
  // Variante treffen. Der Key identifiziert die konkrete Zeile (inkl. _gift-Property)
  // eindeutig.
  const changeLine = (key, q) => mutate(routes.cart_change_url || '/cart/change', { id: String(key), quantity: q });
  const addGift = (id) =>
    mutate(routes.cart_add_url || '/cart/add.js', {
      items: [{ id, quantity: 1, properties: { _gift: 'true' } }],
    });

  /**
   * Eine Geschenk-Zeile ist NUR eine per Chooser hinzugefügte Zeile mit
   * _gift-Property – NICHT jede Zeile, die zufällig dieselbe Variante hat
   * (die könnte ein regulärer Kauf sein).
   */
  function isGift(item) {
    const p = item && item.properties;
    if (!p) return false;
    return p._gift === 'true' || p._gift === true;
  }

  /** Geschenk wählen / tauschen / abwählen. */
  async function selectGift(variantId) {
    if (busy) return;
    busy = true;
    try {
      const cfg = getConfig();
      if (!cfg) return;
      const cart = await getCart();
      const giftLines = cart.items.filter(isGift);
      const already = giftLines.find((i) => i.variant_id === variantId);

      if (already) {
        await changeLine(already.key, 0);
      } else {
        for (const g of giftLines) await changeLine(g.key, 0);
        await addGift(variantId);
      }
    } catch (e) {
      console.warn('[gift-progress] selectGift', e);
    } finally {
      busy = false;
    }
  }

  /** Regeln durchsetzen: unter Schwelle -> entfernen; sonst genau 1 Geschenk, Menge 1. */
  async function enforce() {
    if (busy) return;
    busy = true;
    try {
      const cfg = getConfig();
      if (!cfg) return;
      const cart = await getCart();
      const giftLines = cart.items.filter(isGift);
      if (!giftLines.length) return;

      // Schwellen-Prüfung auf dem tatsächlichen Warenkorbwert OHNE die Geschenk-Zeile.
      // Der native Rabatt macht das Geschenk gratis (Zeilenpreis 0), sobald die
      // Schwelle erreicht ist – das Herausrechnen des Geschenk-Zeilenpreises spiegelt
      // korrekt wider, was der Kunde für die qualifizierenden Artikel zahlt. Gutschein-
      // Codes senken diesen Wert weiterhin (fällt der Wert unter die Schwelle, greift
      // der native Rabatt nicht mehr -> Geschenk muss raus).
      const giftLinesPrice = giftLines.reduce((sum, i) => sum + (i.final_line_price || 0), 0);
      const qualifying = cart.total_price - giftLinesPrice;

      if (qualifying < cfg.threshold) {
        for (const g of giftLines) await changeLine(g.key, 0);
      } else {
        let kept = false;
        for (const g of giftLines) {
          if (!kept) {
            kept = true;
            if (g.quantity !== 1) await changeLine(g.key, 1);
          } else {
            await changeLine(g.key, 0);
          }
        }
      }
    } catch (e) {
      console.warn('[gift-progress] enforce', e);
    } finally {
      busy = false;
    }
  }

  // --- Klick auf eine Geschenk-Option (delegiert, übersteht Re-Renders) -----
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;

    // Klapp-Kopf?
    const header = e.target.closest('.cart-progress__header');
    if (header) {
      e.preventDefault();
      toggleCollapse();
      return;
    }

    // Geschenk-Option?
    const target = e.target.closest('.cart-gift__option');
    if (!target) return;
    e.preventDefault();
    const id = parseInt(target.dataset.giftVariant || '', 10);
    if (id) selectGift(id);
  });

  // Klappzustand initial + nach jedem Re-Render wiederherstellen.
  applyCollapseState();
  document.addEventListener('DOMContentLoaded', applyCollapseState);

  // --- Auf Cart-Änderungen von anderswo reagieren (Auto-Entfernen) ----------
  (async () => {
    try {
      const { StandardEvents } = await import('@shopify/events');
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        const promise = event && event.promise;
        if (!promise) return;
        promise
          .then((resolved) => {
            applyCollapseState(); // nach Re-Render Klappzustand wiederherstellen
            const detail = resolved && resolved.detail;
            if (detail && detail.source === 'gift-progress') return; // eigene Mutation ignorieren
            enforce();
          })
          .catch(() => {});
      });

      // Rabattcode angewendet/entfernt -> Schwelle neu prüfen.
      // Ein Rabatt senkt cart.total_price, löst aber KEIN cartLinesUpdate aus,
      // sondern ein eigenes cartDiscountUpdate (siehe cart-discount.js). Ohne
      // diesen Listener bliebe das Geschenk liegen, obwohl der rabattierte
      // Warenkorbwert unter die Schwelle gefallen ist.
      if (StandardEvents.cartDiscountUpdate) {
        document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
          const promise = event && event.promise;
          if (!promise) {
            enforce();
            return;
          }
          promise
            .then(() => {
              applyCollapseState();
              enforce();
            })
            .catch(() => {});
        });
      }
    } catch (e) {
      console.warn('[gift-progress] Cart-Event-Listener konnte nicht registriert werden.', e);
    }
  })();
})();
